import {computed, CSSProperties} from "vue";
import {EndpointParam} from "@t/fd/layout/Array.ts";

export interface SlotContent {
	label: string;
	help?: string;
	span: number;
	base?: boolean;	// this is a base value - 100%
	perc?: string;
	percStyle?: CSSProperties;
	count?: string;
	countOf?: string;
	action?: string;
	actionArgs?: any[];
	href?: string;			// route or page URL
	hrefParams?: EndpointParam[];	// params of the href url
}

export const VALUE_FORMAT = new Intl.NumberFormat(navigator.language, {
		style: 'decimal',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2
	}
);
export const PERC_FORMAT = new Intl.NumberFormat(navigator.language, {
	style: "percent",
	minimumFractionDigits: 0,
	maximumFractionDigits: 0
});

export interface GridResult {
	R: number
	C: number
	cellWidth: number
	lastRowFill: number
}

interface GridCandidate extends GridResult {
	// internal, same shape — named separately for clarity
}

export function findGrid(n: number, W: number, minW: number, maxW: number): GridResult {
	let best: GridCandidate | null = null;

	if (W < minW) {
		return { R: n, C: 1, cellWidth: W, lastRowFill: 1 };
	}
	if (W > (n * maxW)) {
		return { R: 1, C: n, cellWidth: W/n, lastRowFill: 1 };
	}

	for (let c = 1; c <= n; c++) {
		const cellW = W / c
		if (cellW < minW || cellW > maxW) {
			continue;
		}

		const r = Math.ceil(n / c)
		const lastRowFill = n % c === 0 ? 1 : (n % c) / c

		const candidate: GridCandidate = {
			R: r,
			C: c,
			cellWidth: cellW,
			lastRowFill,
		}

		if (best === null || isBetter(candidate, best, minW, maxW)) {
			best = candidate
		}
	}

	return best ?? { R: n, C: 1, cellWidth: W, lastRowFill: 1 }
}

function isBetter(
	candidate: GridCandidate,
	current: GridCandidate,
	minW: number,
	maxW: number
): boolean {
	if (candidate.R !== current.R)
		return candidate.R < current.R

	if (candidate.lastRowFill !== current.lastRowFill)
		return candidate.lastRowFill > current.lastRowFill

	const midW = (minW + maxW) / 2
	return Math.abs(candidate.cellWidth - midW) < Math.abs(current.cellWidth - midW)
}
